/**
 * Notification engine contracts (Insights › Notifications; admin-managed).
 *
 * A notification RULE watches one PSA entity for newly created or recently updated
 * records, evaluates a set of AND-combined criteria, and emails one message PER matching
 * record (e.g. 4 new unassigned tickets → 4 emails). Detection is poll-based: a frequent
 * job advances each rule's watermark and a dedupe log guarantees a given change emails
 * once. The body/subject are admin-authored templates with `{{field}}` tokens drawn from
 * the watched entity's field catalog.
 *
 * Keep this file free of runtime framework dependencies (shared by backend, frontend, jobs).
 */
/** Entities a notification rule can watch. The backend loader is the source of truth for
 * each entity's available fields; this catalog mirrors it for the UI + validation. */
export const NOTIFICATION_ENTITIES = [
    'tickets',
    'opportunities',
    'time_entries',
    'timesheets',
    'invoices',
    'projects',
    'agreements',
    'customers',
    'contacts',
];
export const NOTIFICATION_ENTITY_LABELS = {
    tickets: 'Tickets',
    opportunities: 'Opportunities',
    time_entries: 'Time Entries',
    timesheets: 'Timesheets',
    invoices: 'Invoices',
    projects: 'Projects',
    agreements: 'Agreements',
    customers: 'Customers',
    contacts: 'Contacts',
};
/** What change fires the rule. `created` = new records; `updated` = recently changed
 * records that match the criteria (re-fires when the record changes again). */
export const NOTIFICATION_TRIGGERS = ['created', 'updated'];
export const NOTIFICATION_TRIGGER_LABELS = {
    created: 'When created',
    updated: 'When updated',
};
/** Comparison operators. Not all apply to every type (see NOTIFICATION_OPERATORS_BY_TYPE). */
export const NOTIFICATION_OPERATORS = [
    'eq',
    'neq',
    'contains',
    'gt',
    'gte',
    'lt',
    'lte',
    'is_true',
    'is_false',
    'is_empty',
    'is_not_empty',
];
export const NOTIFICATION_OPERATOR_LABELS = {
    eq: 'equals',
    neq: 'does not equal',
    contains: 'contains',
    gt: 'greater than',
    gte: 'greater than or equal',
    lt: 'less than',
    lte: 'less than or equal',
    is_true: 'is yes',
    is_false: 'is no',
    is_empty: 'is empty',
    is_not_empty: 'is not empty',
};
/** Operators valid for each field type (UI builds the operator dropdown from this). */
export const NOTIFICATION_OPERATORS_BY_TYPE = {
    string: ['eq', 'neq', 'contains', 'is_empty', 'is_not_empty'],
    number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_empty', 'is_not_empty'],
    boolean: ['is_true', 'is_false'],
    date: ['is_empty', 'is_not_empty'],
    enum: ['eq', 'neq', 'is_empty', 'is_not_empty'],
};
/** Operators that don't take a comparison value. */
export const NOTIFICATION_VALUELESS_OPERATORS = [
    'is_true',
    'is_false',
    'is_empty',
    'is_not_empty',
];
// ---------------------------------------------------------------------------
// Field catalog — the watchable fields per entity. Keys match the backend loader's
// flattened record map and double as `{{key}}` template tokens.
// ---------------------------------------------------------------------------
const TICKET_STATUS_FLAGS = ['open', 'closed'];
export const NOTIFICATION_FIELDS = {
    tickets: [
        { key: 'number', label: 'Ticket #', type: 'number' },
        { key: 'title', label: 'Title', type: 'string' },
        { key: 'status', label: 'Status', type: 'string' },
        { key: 'statusState', label: 'Open / closed', type: 'enum', options: TICKET_STATUS_FLAGS },
        { key: 'priority', label: 'Priority', type: 'string' },
        { key: 'origin', label: 'Origin', type: 'enum', options: ['project', 'support'] },
        { key: 'type', label: 'Type', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'project', label: 'Project', type: 'string' },
        { key: 'assigneeCount', label: 'Assignee count', type: 'number' },
        { key: 'budgetHours', label: 'Budget hours', type: 'number' },
        { key: 'url', label: 'Ticket link', type: 'string' },
    ],
    opportunities: [
        { key: 'name', label: 'Name', type: 'string' },
        { key: 'stage', label: 'Stage', type: 'string' },
        { key: 'status', label: 'Status', type: 'string' },
        { key: 'kind', label: 'New / upsell', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'salesRep', label: 'Sales rep', type: 'string' },
        { key: 'closeDate', label: 'Close date', type: 'date' },
        { key: 'summary', label: 'Summary', type: 'string' },
    ],
    time_entries: [
        { key: 'member', label: 'Member', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'project', label: 'Project', type: 'string' },
        { key: 'ticket', label: 'Ticket', type: 'string' },
        { key: 'serviceCategory', label: 'Service category', type: 'string' },
        { key: 'hours', label: 'Hours', type: 'number' },
        { key: 'billable', label: 'Billable', type: 'boolean' },
        { key: 'workDate', label: 'Work date', type: 'date' },
        { key: 'notes', label: 'Notes', type: 'string' },
    ],
    timesheets: [
        { key: 'member', label: 'Member', type: 'string' },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            options: ['draft', 'submitted', 'approved', 'rejected'],
        },
        { key: 'weekStartDate', label: 'Week start', type: 'date' },
        { key: 'approver', label: 'Approver', type: 'string' },
        { key: 'notes', label: 'Notes', type: 'string' },
    ],
    invoices: [
        { key: 'number', label: 'Invoice #', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            options: ['draft', 'in_review', 'sent', 'closed', 'void'],
        },
        {
            key: 'documentType',
            label: 'Document type',
            type: 'enum',
            options: ['invoice', 'credit_memo'],
        },
        { key: 'source', label: 'Source', type: 'string' },
        { key: 'total', label: 'Total', type: 'number' },
        { key: 'reviewer', label: 'Active reviewer', type: 'string' },
    ],
    projects: [
        { key: 'number', label: 'Project #', type: 'number' },
        { key: 'name', label: 'Name', type: 'string' },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            options: ['active', 'on_hold', 'completed', 'cancelled'],
        },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'projectManager', label: 'Project manager', type: 'string' },
        { key: 'isBillable', label: 'Billable', type: 'boolean' },
    ],
    agreements: [
        { key: 'number', label: 'Agreement #', type: 'number' },
        { key: 'name', label: 'Name', type: 'string' },
        { key: 'type', label: 'Type', type: 'string' },
        { key: 'status', label: 'Status', type: 'enum', options: ['draft', 'active', 'ended'] },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'customerPo', label: 'Customer PO', type: 'string' },
    ],
    customers: [
        { key: 'name', label: 'Name', type: 'string' },
        { key: 'isActive', label: 'Active', type: 'boolean' },
        { key: 'phone', label: 'Phone', type: 'string' },
        { key: 'website', label: 'Website', type: 'string' },
        { key: 'changeControlled', label: 'Change controlled', type: 'boolean' },
    ],
    contacts: [
        { key: 'firstName', label: 'First name', type: 'string' },
        { key: 'lastName', label: 'Last name', type: 'string' },
        { key: 'email', label: 'Email', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'title', label: 'Title', type: 'string' },
        { key: 'isPrimary', label: 'Primary', type: 'boolean' },
    ],
};
/** Dynamic recipient sources per entity (emails resolved from each matching record). */
export const NOTIFICATION_RECIPIENT_SOURCES = {
    tickets: [
        { key: 'assignees', label: 'Ticket assignees' },
        { key: 'projectManager', label: 'Project manager' },
        { key: 'contact', label: 'Ticket contact' },
    ],
    opportunities: [
        { key: 'salesRep', label: 'Sales rep' },
        { key: 'contact', label: 'Opportunity contact' },
    ],
    time_entries: [{ key: 'member', label: 'Member' }],
    timesheets: [
        { key: 'member', label: 'Member' },
        { key: 'approver', label: 'Approver' },
    ],
    invoices: [{ key: 'reviewer', label: 'Active reviewer' }],
    projects: [{ key: 'projectManager', label: 'Project manager' }],
    agreements: [],
    customers: [{ key: 'billingContacts', label: 'Billing contacts' }],
    contacts: [{ key: 'self', label: 'The contact' }],
};
/** Returns the field catalog for an entity, or an empty list for an unknown entity. */
export function notificationFieldsFor(entity) {
    return NOTIFICATION_FIELDS[entity] ?? [];
}
/** Returns the dynamic recipient sources for an entity. */
export function notificationRecipientSourcesFor(entity) {
    return NOTIFICATION_RECIPIENT_SOURCES[entity] ?? [];
}
export const NOTIFICATION_RUN_STATUSES = ['ok', 'failed'];
/** Minimum poll interval; matches the run-due timer cadence. */
export const NOTIFICATION_MAX_RECIPIENTS = 50;
//# sourceMappingURL=notification.js.map